import api from "@/api";
import { defineStore } from "pinia";
import { Learner } from "@/interfaces/learner.interface";
import { NewLearner } from "@/interfaces/new-learner.interface";
import i18n from "@/plugins/i18n";
import { useFilterStore } from "./filterStore";
import { add, set } from "lodash";
import { useDataStore } from "./dataStore";
import { getAuth } from "firebase/auth";
import axios from "axios";
import { getDashboardApiUrl } from "@/lib/runtime-url-resolver";

export const useLearnerStore = defineStore({
  id: "learnerStore",
  state: () => ({
    loader: {
      learner: false,
      emailValidation: false,
    },
    loadingMessage: "",
    schoolLearners: [] as Learner[],
    archiveOptions: false,
    lastFetchTimestamp: 0 as number, // Timestamp of last fetch for cache invalidation
  }),
  getters: {
    getarchiveOptions(): boolean {
      return this.archiveOptions;
    },
    getSchoolLearners(): Learner[] {
      return this.schoolLearners.filter(
        (learner) => learner.status === "active" || learner.status === "deactivated"
      );
    },
    getAllSchoolLearners(): Learner[] {
      return this.schoolLearners;
    },
    getAllActiveSchoolLearners(): Learner[] {
      return this.schoolLearners.filter(
        (learner) => learner.status === "active" || learner.status === "deactivated"
      );
    },
    getLearnerById: (state) => (learnerId: string) => {
      return state.schoolLearners.find((learner) => learner._id === learnerId);
    },
    getLearnersByClassId: (state) => (classId: string) => {
      return state.schoolLearners.filter(
        (learner) => learner.classes && learner.classes.some((cls) => cls._id === classId)
      );
    },
    getLearnersByGrade: (state) => (grade: number) => {
      return state.schoolLearners.filter((learner) => learner.grade === grade);
    },
    getCountOfLearnersByFilters: (state) => {
      const filterStore = useFilterStore();
      const selectedSchool = filterStore.selectedFilters.school;
      const classGroups = filterStore.selectedFilters.classGroup;
      const selectedGrade = filterStore.selectedFilters.gradeLevel;

      let filteredLearners = state.schoolLearners.filter(
        (l) => l.status === "active" || l.status === "deactivated"
      );

      if (selectedSchool) {
        filteredLearners = filteredLearners.filter(
          (learner) => learner.schoolId === selectedSchool
        );
      }

      if (classGroups.length > 0) {
        filteredLearners = filteredLearners.filter((learner) =>
          learner.classes &&
          learner.classes.some((cls) => classGroups.includes(cls._id))
        );
      } else {
        if (selectedGrade && selectedGrade !== -1) {
          filteredLearners = filteredLearners.filter(
            (learner) => learner.grade === selectedGrade
          );
        }
      }

      return filteredLearners.length;
    },
    getLearnerAssessmentBySelectedSubject: (state) => (studentId: string) => {
      const filterStore = useFilterStore();
      const selectedSubject = filterStore.selectedSubject;
      const learner = state.schoolLearners.find((learner) => learner._id === studentId);
      return learner?.assessmentStates.find(
        (assessmentState) => assessmentState.subject.toLowerCase() === selectedSubject.name.toLowerCase()
      );
    }
  },
  actions: {
    setArchiveOptions(value: boolean) {
      this.archiveOptions = value;
    },
    async fetchDecryptedPassword(studentId: string): Promise<string> {
      try {
        const response = await api.get(`/v2/organisation/students/decrypt-password/${studentId}`);
        
        if (response.status !== 200) {
          throw new Error("Failed to decrypt password");
        }
        
        return response.data.password || '';
      } catch (error: any) {
        console.error('Error fetching decrypted password:', error);
        return '';
      }
    },
    async bulkUpdateLearners(learners: any) {
      const filterStore = useFilterStore();

      this.loader.learner = true;
      this.loadingMessage = i18n.global.t(
        "loaders.learner_store.updateLearner"
      );

      try {
        const bulkUpdateLearnersResponse = await api.put(
          "v2/organisation/students/update-multiple",
          learners
        );

        if (bulkUpdateLearnersResponse.status !== 200) {
          throw new Error("Failed to update learners data from API.");
        }

        const updatedLearners = bulkUpdateLearnersResponse.data.data;

        if (updatedLearners?.length > 0) {
          await this.fetchSchoolLearners(filterStore.selectedFilters.school);
        }

        return updatedLearners;
      } catch (error: any) {
        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.loadingMessage = "";
        this.loader.learner = false;
      }
    },
    async updateLearner(learnerId: any, updateClassDetails: any) {
      this.loader.learner = true;
      this.loadingMessage = i18n.global.t(
        "loaders.learner_store.updateLearner"
      );

      try {
        const updateLearnerResponse = await api.put(
          `v2/organisation/students/update/${learnerId[0]}`,
          updateClassDetails
        );

        if (updateLearnerResponse.status !== 200) {
          throw new Error("Failed to update learner data from API.");
        }

        const updatedLearner = updateLearnerResponse.data.data;

        // Find the index of the learner in the schoolLearners array
        const learnerIndex = this.schoolLearners.findIndex(
          (learner) => learner._id === updatedLearner._id
        );

        // If the learner is found, update the object in the array
        if (learnerIndex !== -1) {
          this.schoolLearners.splice(learnerIndex, 1, {
            ...this.schoolLearners[learnerIndex],
            ...updatedLearner,
          });
        } else {
          console.error(
            "Learner not found in schoolLearners array, cannot update."
          );
        }

        return updatedLearner;
      } catch (error: any) {
        // if (error.code) {
        //   throw new Error(error.message);
        // }
        return false;
      } finally {
        this.loadingMessage = "";
        this.loader.learner = false;
      }
    },
    async bulkCreateLearners(learners: NewLearner[]) {
      this.loader.learner = true;
      this.loadingMessage = i18n.global.t(
        "loaders.learner_store.bulkCreateLearners"
      );

      try {
        const bulkCreateLearnersResponse = await api.post(
          "v2/organisation/students/bulk",
          learners
        );

        if (bulkCreateLearnersResponse.status !== 201) {
          throw new Error("Failed to create learners data from API.");
        }

        const bulkCreateLearnersData = bulkCreateLearnersResponse.data.data;

        console.log("bulkCreateLearnersData: ", bulkCreateLearnersData);
        // this.schoolLearners.push(...bulkCreateLearnersData);

        return bulkCreateLearnersData;
      } catch (error: any) {
        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.loadingMessage = "";
        this.loader.learner = false;
      }
    },
    async bulkArchiveUnarchiveLearners(type: string, learners: NewLearner[]) {
      this.loader.learner = true;
      this.loadingMessage = i18n.global.t(
        "loaders.learner_store.bulkArchiveLearners"
      );

      try {
        const bulkArchiveLearnersResponse = await api.post(
          "v2/organisation/students/bulk/archive-unarchive",
          {
            type: type,
            users: learners,
          }
        );

        if (bulkArchiveLearnersResponse.status !== 200) {
          throw new Error("Failed to archive or unarchive learners.");
        }

        const bulkArchiveLearnersData = bulkArchiveLearnersResponse.data;
        console.log("bulkArchiveLearnersData: ", bulkArchiveLearnersData);

        return bulkArchiveLearnersData;
      } catch (error: any) {
        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.loadingMessage = "";
        this.loader.learner = false;
      }
    },
    async createLearner(learner: NewLearner) {
      this.loader.learner = true;
      this.loadingMessage = i18n.global.t(
        "loaders.learner_store.createLearner"
      );

      try {
        const createLearnerResponse = await api.post(
          "v2/organisation/students/new",
          learner
        );

        if (createLearnerResponse.status !== 201) {
          throw new Error("Failed to create learner data from API.");
        }

        const createLearnerData = createLearnerResponse.data.data;
        this.schoolLearners.push(createLearnerData);

        return createLearnerData;
      } catch (error: any) {
        if (error.code) {
          throw new Error(error.message);
        }
        return false;
      } finally {
        this.loadingMessage = "";
        this.loader.learner = false;
      }
    },
    async addLearnerToClass(learnerIds: string, classId: string) {
      this.loader.learner = true;
      this.loadingMessage = i18n.global.t(
        "loaders.learner_store.bulkCreateLearners"
      );

      try {
        const addLearnerToClassResponse = await api.post(
          `v2/organisation/class-group/assign-learners`,
          { classId, learnerIds }
        );

        if (addLearnerToClassResponse.status !== 200) {
          throw new Error("Failed to add learner to class from API.");
        }

        const addLearnerToClassData = addLearnerToClassResponse.data.data;

        const dataStore = useDataStore();
        dataStore.fetchInitialData();

        // Find the index of the learner in the schoolLearners array
        const learnerIndex = this.schoolLearners.findIndex(
          (learner) => learner._id === addLearnerToClassData._id
        );

        // If the learner is found, update the object in the array
        if (learnerIndex !== -1) {
          this.schoolLearners.splice(learnerIndex, 1, {
            ...this.schoolLearners[learnerIndex],
            ...addLearnerToClassData,
          });
        } else {
          console.error(
            "Learner not found in schoolLearners array, cannot update."
          );
        }

        return addLearnerToClassData;
      } catch (error: any) {
        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.loadingMessage = "";
        this.loader.learner = false;
      }
    },
    async fetchSchoolLearners(schoolId: string) {
      this.loader.learner = true;
      this.loadingMessage = i18n.global.t(
        "loaders.learner_store.fetchSchoolLearners"
      );

      try {
        const learnersResponse = await api.get(
          `v2/organisation/students/school/${schoolId}`
        );

        if (learnersResponse.status !== 200) {
          throw new Error("Failed to fetch learners data from API.");
        }

        const learnersData = learnersResponse.data.data;
        this.schoolLearners = learnersData;
        this.lastFetchTimestamp = Date.now();
      } catch (error: any) {
        console.log("Error: ", error);

        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.loadingMessage = "";
        this.loader.learner = false;
      }
    },
    async validateEmail(email: string) {
      this.loader.emailValidation = true;
      this.loadingMessage = i18n.global.t(
        "loaders.learner_store.validateEmail"
      );

      try {
        const emailValidationResponse = await api.get(
          `/v2/organisation/students/validate-email?email=${encodeURIComponent(
            email
          )}`
        );

        if (emailValidationResponse.status !== 200) {
          throw new Error("Failed to validate email from API.");
        }

        const emailValidationData = emailValidationResponse.data.exists;
        return emailValidationData;
      } catch (error: any) {
        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.loadingMessage = "";
        this.loader.emailValidation = false;
      }
    },

    /**
     * Validates a batch of emails against the database.
     * @param emails - An array of email strings to validate.
     * @returns A promise that resolves to an array of emails that already exist.
     */
    async bulkValidateEmails(emails: string[]): Promise<string[]> {
      if (emails.length === 0) {
        return [];
      }
      this.loader.emailValidation = true;
      try {
        const response = await api.post('/v2/organisation/students/bulk-validate-emails', { emails });
        
        if (response.status !== 200) {
          throw new Error("Failed to bulk validate emails from API.");
        }
        
        // Return the array of emails that were found to exist
        return response.data.existingEmails || [];
      } catch (error: any) {
        console.error("Bulk email validation failed:", error);
        throw new Error(error.message || "An error occurred during bulk email validation.");
      } finally {
        this.loader.emailValidation = false;
      }
    },

    async downloadSchoolPasswords(schoolId: string, grade?: number, schoolName?: string, classId?: string) {
      this.loader.learner = true;
      try {
        let url = `v2/organisation/students/print-passwords?schoolId=${schoolId}`;
        if (typeof grade !== "undefined") {
          url += `&grade=${grade}`;
        }
        if (classId) {
          url += `&classId=${classId}`;
        }
        const response = await api.get(url, {
          responseType: 'blob'
        });
        // Check for success:true in the response
        if (response.status !== 200) {
          throw new Error("Failed to print passwords from API.");
        }
        console.log(response);
        const blob = new Blob([response.data], { type: 'application/zip' });
        const urlDownload = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = urlDownload;
        let downloadName = `Print Logins for ${schoolName || ''}`;
        if (grade) downloadName += ` - Grade ${grade}`;
        downloadName += '.zip';
        link.download = downloadName;
        link.click();
        URL.revokeObjectURL(urlDownload);
      } catch (error: any) {
        if (error.code) {
          throw new Error(error.message);
        }
      } finally {
        this.loader.learner = false;
      }
    },
    /**
     * Fetches all learners from a school for export.
     * Uses a dedicated API instance with a longer timeout.
     * @param schoolId The ID of the school to export learners from.
     * @returns A promise that resolves to an array of learners.
     */
    async exportSchoolLearners(schoolId: string): Promise<Learner[]> {
      // Create a new axios instance with a longer timeout for this specific call
      const exportApi = axios.create({
        baseURL: getDashboardApiUrl(),
        timeout: 600000, // 5 minutes timeout
        headers: {
          "Content-Type": "application/json",
        },
      });

      // Add the authorization interceptor to the new instance
      exportApi.interceptors.request.use(
        async (config) => {
          const auth = getAuth();
          const token = await auth.currentUser?.getIdToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
          return config;
        },
        (error) => Promise.reject(error)
      );

      this.loader.learner = true;
      this.loadingMessage = "Exporting learners, this may take a moment...";

      try {
        const response = await exportApi.get(
          `/v2/organisation/students/export/school/${schoolId}`
        );

        if (response.status !== 200) {
          throw new Error("Failed to fetch learners data for export from API.");
        }

        return response.data.data || [];
      } catch (error: any) {
        console.error("Error exporting school learners:", error);
        // Re-throw the error to be caught in the component
        throw new Error(error.response?.data?.error || "An error occurred during export.");
      } finally {
        this.loadingMessage = "";
        this.loader.learner = false;
      }
    },

    async triggerReassessment(learnerIds: string[]) {
      const response = await api.post("/v2/organisation/students/trigger-reassessment", {
        learnerIds,
      });
      return response.data;
    },
  },
  persist: true,
});
